import './index.scss'
import { useEffect, useState, useRef, useLayoutEffect, useCallback, useMemo } from 'react'
import { faJsSquare, faReact, faSwift } from '@fortawesome/free-brands-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { prepareWithSegments, layoutNextLine } from '@chenglou/pretext'
import claudeImg from '../../assets/images/claude.png'
import pythonImg from '../../assets/images/python.png'
import pytorchImg from '../../assets/images/pytorch.png'
import AnimatedLetters from '../AnimatedLetters'
import Experience from '../Experience'
import Loader from 'react-loaders'

const FONT = "300 18px 'Helvetica Neue', sans-serif"
const LINE_HEIGHT = 25
const PARA_GAP = 20

const PARAGRAPHS = [
  "I'm a software engineer with a Master's in Computer Science from San Jose State University, focused on machine learning, data-driven systems, and full-stack development.",
  "I currently work as a full-stack software engineer at Volkswagen Group of America Innovation & Engineering Center California, where I began as an intern. My experience also includes software engineering at ISSNAF and teaching CS and calculus at C2 Education, giving me a well-rounded perspective on both building and communicating complex systems.",
  "I work across the full stack and care deeply about clean code, intuitive interfaces, and the intersection of ML and human-computer interaction. I value thoughtful design and seamless user experiences, principles I've admired in Apple's philosophy since my first job at an Apple Store.",
  "Have fun playing with the cube :)",

]

// Compute line-by-line layout for all paragraphs using pretext.
// obstacleBox is relative to the top of the paragraphs container.
function computeLayout(textWidth, obstacleBox) {
  if (!textWidth || textWidth <= 0) return null

  const result = []
  let cursorTop = 0

  for (const text of PARAGRAPHS) {
    const prepared = prepareWithSegments(text, FONT)
    const lines = []
    let cursor = { segmentIndex: 0, graphemeIndex: 0 }
    let lineIndex = 0
    let skipStreak = 0

    while (skipStreak < 60) {
      const lineRelY = lineIndex * LINE_HEIGHT
      const lineAbsTop = cursorTop + lineRelY
      const lineAbsBot = lineAbsTop + LINE_HEIGHT

      let lineX = 0
      let lineW = textWidth

      if (obstacleBox) {
        const { left: oL, right: oR, top: oT, bottom: oB } = obstacleBox
        if (oT < lineAbsBot && oB > lineAbsTop && oR > 0 && oL < textWidth) {
          const spaceLeft = oL - 6
          const spaceRight = textWidth - oR - 6
          if (spaceLeft >= spaceRight) {
            lineW = Math.max(0, spaceLeft)
          } else {
            lineX = oR + 6
            lineW = Math.max(0, spaceRight)
          }
        }
      }

      if (lineW < 40) {
        lineIndex++
        skipStreak++
        continue
      }
      skipStreak = 0

      const line = layoutNextLine(prepared, cursor, lineW)
      if (!line) break

      lines.push({ text: line.text, x: lineX, y: lineRelY })
      cursor = line.end
      lineIndex++
    }

    const paraHeight = lineIndex * LINE_HEIGHT
    result.push({ lines, height: paraHeight })
    cursorTop += paraHeight + PARA_GAP
  }

  return result
}

const About = () => {
  const [letterClass, setLetterClass] = useState('text-animate')
  const [cubeOffset, setCubeOffset] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [obstacleBox, setObstacleBox] = useState(null)
  const [textWidth, setTextWidth] = useState(0)
  const [shootPhase, setShootPhase] = useState(null) // null | 'shooting' | 'placed'

  const cubeWrapperRef = useRef(null)
  const textParagraphsRef = useRef(null)
  const textZoneRef = useRef(null)
  const aboutHeroRef = useRef(null)

  // Physics state — all in refs to avoid triggering re-renders inside the loop
  const physicsRef = useRef({ x: 0, y: 0, vx: 0, vy: 0 })
  const targetRef = useRef({ x: 0, y: 0 })
  const isDraggingRef = useRef(false)
  const rafRef = useRef(null)
  const hasDraggedRef = useRef(false)
  const dragStartRef = useRef({ startX: 0, startY: 0, startOffX: 0, startOffY: 0 })

  useEffect(() => {
    setTimeout(() => setLetterClass('text-animate-hover'), 2400)
  }, [])

  // After 5s, shoot the cube into the text if the user hasn't dragged it yet
  useEffect(() => {
    const timer = setTimeout(() => {
      if (hasDraggedRef.current) return
      if (!cubeWrapperRef.current || !textParagraphsRef.current) return

      const cubeRect = cubeWrapperRef.current.getBoundingClientRect()
      const textRect = textParagraphsRef.current.getBoundingClientRect()

      const targetCX = textRect.right - 120
      const targetCY = textRect.top + 60
      const cubeCX = cubeRect.left + cubeRect.width / 2
      const cubeCY = cubeRect.top + cubeRect.height / 2

      const newX = physicsRef.current.x + (targetCX - cubeCX)
      const newY = physicsRef.current.y + (targetCY - cubeCY)

      // Sync physics to destination so drag starts correctly after the transition
      physicsRef.current = { x: newX, y: newY, vx: 0, vy: 0 }
      targetRef.current = { x: newX, y: newY }

      setCubeOffset({ x: newX, y: newY })
      setShootPhase('shooting')
    }, 5000)
    return () => clearTimeout(timer)
  }, [])

  // Spring physics loop — runs every rAF frame while the cube is moving
  const startPhysicsLoop = useCallback(() => {
    if (rafRef.current) return

    const tick = () => {
      const p = physicsRef.current
      const t = targetRef.current
      const dragging = isDraggingRef.current

      // Tighter spring while dragging (responsive), looser on release (floaty coast)
      const stiffness = dragging ? 0.14 : 0.08
      const damping   = dragging ? 0.76 : 0.87

      p.vx = (p.vx + (t.x - p.x) * stiffness) * damping
      p.vy = (p.vy + (t.y - p.y) * stiffness) * damping
      p.x += p.vx
      p.y += p.vy

      // Safezone boundary: bounce off the About-me section edges
      const el = cubeWrapperRef.current
      const hero = aboutHeroRef.current
      if (el && hero) {
        const r    = el.getBoundingClientRect()
        const zone = hero.getBoundingClientRect()
        const pad  = 16

        if (r.left < zone.left + pad) {
          p.x += (zone.left + pad) - r.left
          p.vx = Math.abs(p.vx) * 0.35
          t.x = Math.max(t.x, p.x)
        } else if (r.right > zone.right - pad) {
          p.x -= r.right - (zone.right - pad)
          p.vx = -Math.abs(p.vx) * 0.35
          t.x = Math.min(t.x, p.x)
        }

        if (r.top < zone.top + pad) {
          p.y += (zone.top + pad) - r.top
          p.vy = Math.abs(p.vy) * 0.35
          t.y = Math.max(t.y, p.y)
        } else if (r.bottom > zone.bottom - pad) {
          p.y -= r.bottom - (zone.bottom - pad)
          p.vy = -Math.abs(p.vy) * 0.35
          t.y = Math.min(t.y, p.y)
        }
      }

      // Tilt the cube wrapper based on velocity — direct DOM for performance
      if (cubeWrapperRef.current) {
        const tiltZ = Math.max(-22, Math.min(22, -p.vx * 3.0))
        const tiltX = Math.max(-15, Math.min(15,  p.vy * 2.5))
        cubeWrapperRef.current.style.transform = `rotateZ(${tiltZ}deg) rotateX(${tiltX}deg)`
      }

      setCubeOffset({ x: p.x, y: p.y })

      const settled =
        !dragging &&
        Math.abs(p.vx) < 0.04 &&
        Math.abs(p.vy) < 0.04 &&
        Math.abs(t.x - p.x) < 0.04 &&
        Math.abs(t.y - p.y) < 0.04

      if (!settled) {
        rafRef.current = requestAnimationFrame(tick)
      } else {
        rafRef.current = null
        p.vx = 0
        p.vy = 0
        p.x = t.x
        p.y = t.y
        setCubeOffset({ x: t.x, y: t.y })
        // Return tilt to neutral once settled
        if (cubeWrapperRef.current) {
          cubeWrapperRef.current.style.transform = ''
        }
      }
    }

    rafRef.current = requestAnimationFrame(tick)
  }, [])

  // Cancel any pending rAF on unmount
  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [])

  // Measure text zone width on mount and resize
  useLayoutEffect(() => {
    if (textZoneRef.current) {
      setTextWidth(textZoneRef.current.getBoundingClientRect().width)
    }
  }, [])

  useEffect(() => {
    const onResize = () => {
      if (textZoneRef.current) {
        setTextWidth(textZoneRef.current.getBoundingClientRect().width)
      }
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Re-measure obstacle box whenever the cube settles at a new position
  useLayoutEffect(() => {
    if (!cubeWrapperRef.current || !textParagraphsRef.current) return
    const cubeRect = cubeWrapperRef.current.getBoundingClientRect()
    const paraRect = textParagraphsRef.current.getBoundingClientRect()
    setObstacleBox({
      left:   cubeRect.left   - paraRect.left,
      top:    cubeRect.top    - paraRect.top,
      right:  cubeRect.right  - paraRect.left,
      bottom: cubeRect.bottom - paraRect.top,
    })
  }, [cubeOffset, shootPhase])

  // Shared drag-start logic for both mouse and touch
  const startDrag = useCallback((clientX, clientY) => {
    hasDraggedRef.current = true
    isDraggingRef.current = true
    setShootPhase(null)
    setIsDragging(true)

    dragStartRef.current = {
      startX:    clientX,
      startY:    clientY,
      startOffX: physicsRef.current.x,
      startOffY: physicsRef.current.y,
    }

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
    targetRef.current = { x: physicsRef.current.x, y: physicsRef.current.y }
    startPhysicsLoop()
  }, [startPhysicsLoop])

  // Shared move logic
  const moveDrag = useCallback((clientX, clientY) => {
    if (!isDraggingRef.current) return
    const { startX, startY, startOffX, startOffY } = dragStartRef.current
    targetRef.current = {
      x: startOffX + (clientX - startX),
      y: startOffY + (clientY - startY),
    }
    startPhysicsLoop()
  }, [startPhysicsLoop])

  // Shared release logic
  const endDrag = useCallback(() => {
    if (!isDraggingRef.current) return
    isDraggingRef.current = false
    setIsDragging(false)

    // Throw: extend the target in the direction of current velocity so the cube
    // overshoots and floats to a stop rather than snapping dead.
    const p = physicsRef.current
    targetRef.current = {
      x: targetRef.current.x + p.vx * 5,
      y: targetRef.current.y + p.vy * 5,
    }
    startPhysicsLoop()
  }, [startPhysicsLoop])

  // Global mouse handlers
  useEffect(() => {
    const onMouseMove = (e) => moveDrag(e.clientX, e.clientY)
    const onMouseUp   = ()  => endDrag()

    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup',   onMouseUp)
    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup',   onMouseUp)
    }
  }, [moveDrag, endDrag])

  // Global touch handlers — passive:false on touchmove so we can preventDefault
  useEffect(() => {
    const onTouchMove = (e) => {
      if (!isDraggingRef.current) return
      e.preventDefault()
      moveDrag(e.touches[0].clientX, e.touches[0].clientY)
    }
    const onTouchEnd = () => endDrag()

    window.addEventListener('touchmove', onTouchMove, { passive: false })
    window.addEventListener('touchend',  onTouchEnd)
    return () => {
      window.removeEventListener('touchmove', onTouchMove)
      window.removeEventListener('touchend',  onTouchEnd)
    }
  }, [moveDrag, endDrag])

  const onMouseDown = (e) => {
    e.preventDefault()
    startDrag(e.clientX, e.clientY)
  }

  const onTouchStart = (e) => {
    startDrag(e.touches[0].clientX, e.touches[0].clientY)
  }

  const paragraphData = computeLayout(textWidth, obstacleBox)

  // Pin to no-obstacle height + cube-wrap buffer so cube reflow can't grow
  // .about-hero and shift the Experience section below it.
  const lockedTextHeight = useMemo(() => {
    if (!textWidth) return 0
    const baseline = computeLayout(textWidth, null)
    if (!baseline) return 0
    const baseTotal = baseline.reduce((sum, p) => sum + p.height + PARA_GAP, 0)
    return baseTotal + 220
  }, [textWidth])

  return (
    <>
    <div className='container about-page'>
      <div ref={aboutHeroRef} className='about-hero'>
        <div ref={textZoneRef} className='text-zone'>
          <h1>
            <AnimatedLetters
              letterClass={letterClass}
              strArray={['A', 'b', 'o', 'u', 't', ' ', 'm', 'e']}
              idx={15}
              showCursor
            />
          </h1>
          <div
            ref={textParagraphsRef}
            className='text-paragraphs'
            style={{ minHeight: lockedTextHeight }}
          >
            {paragraphData
              ? paragraphData.map((para, pi) => (
                  <div
                    key={pi}
                    style={{ position: 'relative', height: para.height + PARA_GAP }}
                  >
                    {para.lines.map((line, li) => (
                      <span
                        key={li}
                        className='pretext-line'
                        style={{ left: line.x, top: line.y }}
                      >
                        {line.text}
                      </span>
                    ))}
                  </div>
                ))
              : PARAGRAPHS.map((text, pi) => <p key={pi}>{text}</p>)}
          </div>
        </div>

        <div
          className={`stage-cube-cont${isDragging ? ' grabbing' : ''}`}
          style={{
            transform: `translate(${cubeOffset.x}px, ${cubeOffset.y}px)`,
            transition: shootPhase === 'shooting' ? 'transform 0.7s cubic-bezier(0.33, 1, 0.68, 1)' : 'none',
          }}
          onMouseDown={onMouseDown}
          onTouchStart={onTouchStart}
          onTransitionEnd={() => { if (shootPhase === 'shooting') setShootPhase('placed') }}
        >
          <div ref={cubeWrapperRef} className={`cube-drag-wrapper${shootPhase === 'placed' ? ' shaking' : ''}`}>
            <div className='cubespinner'>
              <div className='face1'>
                <FontAwesomeIcon icon={faSwift} color='#DD0031' />
              </div>
              <div className='face2'>
                <img src={claudeImg} alt='Claude' className='cube-img' />
              </div>
              <div className='face3'>
                <img src={pythonImg} alt='Python' className='cube-img' />
              </div>
              <div className='face4'>
                <FontAwesomeIcon icon={faReact} color='#5ED4F4' />
              </div>
              <div className='face5'>
                <FontAwesomeIcon icon={faJsSquare} color='#EFD81D' />
              </div>
              <div className='face6'>
                <img src={pytorchImg} alt='PyTorch' className='cube-img' />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Experience />
    </div>
    <Loader type='pacman' />
    </>
  )
}

export default About
